import { Link, useLocation, useNavigate } from "react-router-dom";
import { Mail } from "lucide-react";
import Logo, { LogoMark } from "./Logo";
import { WhatsAppIcon } from "./WhatsAppIcon";
import { useContent } from "@/context/ContentContext";
import { scrollToSection, waLink } from "@/utils/scroll";

export default function Footer() {
  const { content } = useContent();
  const navigate = useNavigate();
  const location = useLocation();
  const s = content?.settings;
  const plans = content?.plans || [];

  const go = (id) => {
    if (location.pathname !== "/") {
      navigate("/");
      setTimeout(() => scrollToSection("#" + id), 150);
    } else scrollToSection("#" + id);
  };

  const wa = waLink(s?.whatsapp_intl, "Hello IPTV HUB,");

  return (
    <footer className="relative border-t border-slate-900/10 bg-slate-100/60" data-testid="footer">
      <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
        <div className="grid gap-12 md:grid-cols-2 lg:grid-cols-4">
          <div className="space-y-5">
            <Logo />
            <p className="max-w-xs text-sm leading-relaxed text-slate-500">
              {s?.brand_description || ""}
            </p>
            <a
              href={wa}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-whatsapp-link"
              className="inline-flex items-center gap-2 rounded-full border border-emerald-400/30 bg-emerald-500/10 px-4 py-2 text-sm font-semibold text-emerald-300 transition-all hover:bg-emerald-500/20"
            >
              <WhatsAppIcon className="h-4 w-4" />
              {s?.whatsapp_display || "WhatsApp"}
            </a>
          </div>

          <div>
            <h4 className="mb-5 font-display text-sm font-bold uppercase tracking-[0.2em] text-slate-900">Quick Links</h4>
            <ul className="space-y-3 text-sm text-slate-500">
              {[["Home", "top"], ["Plans", "plans"], ["Features", "features"], ["How It Works", "how"], ["FAQ", "faq"], ["Contact", "contact"]].map(([label, id]) => (
                <li key={id}>
                  <button data-testid={`footer-${id}-link`} onClick={() => go(id)} className="transition-colors hover:text-electric">
                    {label}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="mb-5 font-display text-sm font-bold uppercase tracking-[0.2em] text-slate-900">Plans</h4>
            <ul className="space-y-3 text-sm text-slate-500">
              {plans.map((p) => (
                <li key={p.id}>
                  <Link data-testid={`footer-plan-${p.id}-link`} to={`/order?plan=${p.id}`} className="transition-colors hover:text-electric">
                    {p.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="mb-5 font-display text-sm font-bold uppercase tracking-[0.2em] text-slate-900">Legal & Contact</h4>
            <ul className="space-y-3 text-sm text-slate-500">
              <li><Link data-testid="footer-privacy-link" to="/privacy" className="transition-colors hover:text-electric">Privacy Policy</Link></li>
              <li><Link data-testid="footer-terms-link" to="/terms" className="transition-colors hover:text-electric">Terms & Conditions</Link></li>
              <li><Link data-testid="footer-refund-link" to="/refund" className="transition-colors hover:text-electric">Refund Policy</Link></li>
              <li>
                <a href={`mailto:${s?.email || ""}`} data-testid="footer-email-link" className="inline-flex items-center gap-2 transition-colors hover:text-electric">
                  <Mail className="h-4 w-4" /> {s?.email || ""}
                </a>
              </li>
              <li><Link data-testid="footer-admin-link" to="/admin" className="text-slate-600 transition-colors hover:text-slate-500">Admin</Link></li>
            </ul>
          </div>
        </div>

        <div className="mt-14 flex flex-col items-center justify-between gap-4 border-t border-slate-900/10 pt-8 sm:flex-row">
          <div className="flex items-center gap-2 text-sm text-slate-500">
            <LogoMark className="h-6 w-6" />
            <span>Copyright © {new Date().getFullYear()} IPTV HUB. All rights reserved.</span>
          </div>
          <p className="font-mono text-xs tracking-widest text-slate-600">YOUR ENTERTAINMENT. ONE POWERFUL HUB.</p>
        </div>
      </div>
    </footer>
  );
}
